import { useCallback, useEffect, useRef, useState } from 'react'

import { exchangeAgnosticRateToken } from 'auth/use-cases/exchangeAgnosticRateToken'

import { useAbortController } from '../../../ui/hooks/useAbortController'
import { type Chat, type ListChatSource } from '../../domain/Chat'
import { getOrCreateConversation } from '../../use-cases/getOrCreateConversation'
import { getUserContactInfo } from '../../use-cases/getUserContactInfo'
import { type UseChatReturn } from './useChat'

type Me = Awaited<ReturnType<typeof getUserContactInfo>>

export type ListChatResolvedCreate = {
  shipper: ListChatSource['shipper']
  subject: string
}

export type UseListChatProps = {
  source: ListChatSource
  resolveShipper: () => Promise<ListChatResolvedCreate>
}

export function useListChat({ source, resolveShipper }: UseListChatProps): UseChatReturn {
  const [isLoading, setIsLoading] = useState(true)
  const [conversation, setConversation] = useState<Chat>()
  const [me, setMe] = useState<Me>()
  const [subject, setSubject] = useState(source.subject)
  const abortController = useAbortController()
  const isOpeningConversation = useRef(false)
  const chatToken = useRef<string>()

  const subtitle =
    conversation?.participants && conversation.participants.length > 0
      ? conversation.participants.map(({ name }) => name).join(', ')
      : source.subtitle

  const initialize = useCallback(async () => {
    setIsLoading(true)

    try {
      if (!source.inviteToken) {
        throw new Error('Missing invite token for list chat')
      }

      const token = await exchangeAgnosticRateToken({
        entityIdentifier: source.entityIdentifier,
        accessToken: source.inviteToken,
      })
      chatToken.current = token
      const chatUser = await getUserContactInfo(abortController, token)
      setMe(chatUser)
    } catch {
      setMe(undefined)
    } finally {
      setIsLoading(false)
    }
  }, [source.entityIdentifier, source.inviteToken, abortController])

  const openConversation = useCallback(async () => {
    if (isOpeningConversation.current) return

    isOpeningConversation.current = true

    try {
      // Always PUT, even for existing chats: chat-backend joins the carrier when missing.
      const resolved = await resolveShipper()
      setSubject(resolved.subject)
      const conversationResponse = await getOrCreateConversation(
        {
          carrier: me,
          subject: resolved.subject,
          shipper: resolved.shipper,
          entityIdentifier: source.entityIdentifier,
        },
        undefined,
        chatToken.current
      )

      setConversation(conversationResponse)
    } finally {
      isOpeningConversation.current = false
    }
  }, [me, resolveShipper, source.entityIdentifier])

  const onUnreadsChange = useCallback(async () => {
    // List chat opens a specific shipment conversation via openConversation.
    // Do not pick an unscoped chat from the full list.
  }, [])

  useEffect(() => {
    initialize()
  }, [initialize])

  return {
    conversation,
    subject,
    subtitle,
    isLoading,
    me,
    openConversation,
    onUnreadsChange,
  }
}
